import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Add, PersonAdd } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '../portalTheme';
import { ErrorState, LoadingState, StatusChip, WealthGraphic, useAsync, usePortalContext } from '../PortalUi';
import { fetchActiveDebits, fetchOutstanding, fetchPayments } from '../../../../services/request/CustomerPortal';
import { firstName, formatDate, inr, ordinalDay } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalHome() {
  const { customerName } = usePortalContext();
  const { t, term } = useTranslation();
  const summary = useAsync(
    () => Promise.all([fetchOutstanding(), fetchActiveDebits(), fetchPayments(0, 5)]),
    [],
  );

  const [outstanding, debits = [], payments] = summary.data ?? [];
  const name = firstName(customerName);

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Paper
        sx={{
          bgcolor: brand.navy,
          color: brand.ivory,
          border: 'none',
          p: { xs: 3, md: 5 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: brand.gold }}>
            {t('portal.home.welcome')}
          </Typography>
          <Typography variant="h4" sx={{ mb: 3 }}>
            {name ? t('portal.home.hello', { name }) : t('portal.home.helloFallback')}
          </Typography>
          <Typography sx={{ color: brand.mist }}>{t('portal.home.totalOutstanding')}</Typography>
          <Typography variant="h3" sx={{ my: 1 }}>
            {summary.loading && !summary.data ? '…' : inr(outstanding)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
            <Button component={RouterLink} to="/customer/request-funds" variant="contained" color="secondary" startIcon={<Add />}>
              {t('portal.home.requestFunds')}
            </Button>
            <Button
              component={RouterLink}
              to="/customer/referrals"
              variant="outlined"
              startIcon={<PersonAdd />}
              sx={{ color: brand.ivory, borderColor: 'rgba(249,248,245,0.4)', '&:hover': { borderColor: brand.gold } }}
            >
              {t('portal.home.refer')}
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
          <WealthGraphic size={260} />
        </Box>
      </Paper>

      {summary.error ? <ErrorState message={summary.error} onRetry={summary.reload} /> : null}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('portal.home.activeDebits')}</Typography>
            <Button component={RouterLink} to="/customer/debits" size="small" color="secondary">
              {t('common.viewAll')}
            </Button>
          </Box>
          {summary.loading && !summary.data ? (
            <LoadingState label={t('portal.home.loadingDebits')} />
          ) : debits.length === 0 ? (
            <Typography color="text.secondary">{t('portal.home.noActiveDebits')}</Typography>
          ) : (
            debits.slice(0, 3).map(debit => {
              const day = ordinalDay(debit.paymentDate);
              return (
                <Box
                  key={debit.id}
                  component={RouterLink}
                  to="/customer/debits"
                  state={{ openDebitId: debit.id }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${brand.border}`,
                    color: 'inherit',
                    textDecoration: 'none',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{term('debitType', debit.type, t('portal.common.debit'))}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {day ? t('portal.common.interestDateEachMonth', { day }) : t('portal.common.interestDateMissing')}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 700 }}>{inr(debit.outstanding)}</Typography>
                    <StatusChip status={debit.status} />
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('portal.home.recentPayments')}</Typography>
            <Button component={RouterLink} to="/customer/payments" size="small" color="secondary">
              {t('common.viewAll')}
            </Button>
          </Box>
          {summary.loading && !summary.data ? (
            <LoadingState label={t('portal.home.loadingPayments')} />
          ) : !payments || payments.items.length === 0 ? (
            <Typography color="text.secondary">{t('portal.home.noPayments')}</Typography>
          ) : (
            payments.items.map(payment => (
              <Box
                key={payment.id}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.5, borderBottom: `1px solid ${brand.border}` }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{term('paymentType', payment.paymentType, t('portal.common.payment'))}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(payment.createdDate) ?? t('portal.common.dateMissing')}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700 }}>{inr(payment.amount)}</Typography>
              </Box>
            ))
          )}
        </Paper>
      </Box>
    </Box>
  );
}
